<template>
  <TransitionRoot
    as="template"
    :show="open"
  >
    <Dialog
      as="div"
      class="fixed inset-0 z-[1600] overflow-hidden"
      @close="closeModal"
    >
      <audio
        ref="fireworksSound"
        :src="fireworksSoundSrc"
      />

      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/80 backdrop-blur-[2px]">
          <div class="absolute inset-0 pointer-events-none overflow-hidden">
            <span
              v-for="(burst, idx) in fireworkBursts"
              :key="`creator-member-join-firework-${idx}`"
              class="firework-burst"
              :style="{
                left: burst.x,
                top: burst.y,
                animationDelay: burst.delay,
                animationDuration: burst.duration,
                '--firework-color': burst.color,
              }"
            />
          </div>
        </div>
      </TransitionChild>

      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        enter-to="opacity-100 translate-y-0 sm:scale-100"
        leave="ease-in duration-200"
        leave-from="opacity-100 translate-y-0 sm:scale-100"
        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
      >
        <DialogPanel class="relative mx-auto mt-16 w-[92%] max-w-4xl rounded-2xl bg-transparent p-6 text-center">
          <button
            type="button"
            class="absolute right-4 top-4 rounded-full p-2 text-zinc-300 transition hover:bg-zinc-100 hover:text-zinc-600"
            @click="closeModal"
          >
            <XMarkIcon class="h-7 w-7" />
          </button>

          <DialogTitle class="pt-6 text-4xl font-semibold text-zinc-50 sm:text-6xl">
            Congratulations!
          </DialogTitle>
          <p class="mt-6 text-2xl font-medium text-zinc-100 sm:text-4xl">
            You have a new member!
          </p>
          <p class="mt-4 text-2xl font-semibold text-[#2D8CFF] sm:text-5xl">
            {{ displayMemberName }}
          </p>

          <div class="mx-auto mt-10 flex w-full justify-center">
            <button
              v-if="hasLinkPath"
              type="button"
              class="w-full rounded-md bg-[#2d8bff] px-6 py-4 text-lg font-semibold text-white shadow-sm transition hover:bg-[#2d8bffce] sm:w-[280px]"
              @click="handleViewMember"
            >
              View Member
            </button>
          </div>
        </DialogPanel>
      </TransitionChild>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';
import { XMarkIcon } from '@heroicons/vue/24/outline';
import fireworksSoundSrc from '@/assets/media/audio/fireworks-sound.mp3';

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  memberName: {
    type: String,
    default: '',
  },
  linkPath: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:open', 'view-member']);
const fireworksSound = ref(null);
const fireworkBursts = [
  { x: '14%', y: '22%', delay: '0s', duration: '2.8s', color: '#2d8bff' },
  { x: '26%', y: '38%', delay: '0.35s', duration: '2.5s', color: '#22c55e' },
  { x: '38%', y: '20%', delay: '0.65s', duration: '2.7s', color: '#f59e0b' },
  { x: '51%', y: '34%', delay: '0.2s', duration: '2.9s', color: '#e879f9' },
  { x: '64%', y: '23%', delay: '0.9s', duration: '2.6s', color: '#38bdf8' },
  { x: '76%', y: '37%', delay: '0.5s', duration: '2.8s', color: '#f43f5e' },
  { x: '88%', y: '20%', delay: '1.05s', duration: '2.7s', color: '#a3e635' },
  { x: '19%', y: '62%', delay: '0.8s', duration: '2.9s', color: '#fb7185' },
  { x: '34%', y: '72%', delay: '1.2s', duration: '2.8s', color: '#60a5fa' },
  { x: '52%', y: '64%', delay: '1.45s', duration: '2.6s', color: '#34d399' },
  { x: '70%', y: '73%', delay: '1.15s', duration: '2.9s', color: '#facc15' },
  { x: '83%', y: '61%', delay: '1.55s', duration: '2.7s', color: '#c084fc' },
];

const displayMemberName = computed(() => {
  const value = String(props.memberName || '').trim();
  return value || 'New Member';
});

const hasLinkPath = computed(() => Boolean(String(props.linkPath || '').trim()));

const closeModal = () => {
  emit('update:open', false);
  if (fireworksSound.value) {
    fireworksSound.value.pause();
    fireworksSound.value.currentTime = 0;
  }
};

const handleViewMember = () => {
  emit('view-member', String(props.linkPath || '').trim());
  closeModal();
};

const startFireworks = async () => {
  await nextTick();
  if (!fireworksSound.value) return;
  fireworksSound.value.volume = 0.04;
  try {
    await fireworksSound.value.play();
  } catch (_error) {
    // autoplay may be blocked; keep modal functional
  }
};

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      void startFireworks();
    } else if (fireworksSound.value) {
      fireworksSound.value.pause();
      fireworksSound.value.currentTime = 0;
    }
  }
);
</script>

<style scoped>
.firework-burst {
  position: absolute;
  width: 10px;
  height: 10px;
  color: var(--firework-color, #2d8bff);
  transform: translate(-50%, -50%) scale(0.2);
  opacity: 0;
  animation-name: firework-burst;
  animation-timing-function: ease-out;
  animation-iteration-count: infinite;
}

.firework-burst::before,
.firework-burst::after {
  content: '';
  position: absolute;
  inset: -1px;
  border-radius: 999px;
}

.firework-burst::before {
  box-shadow:
    0 -28px 0 0 currentColor,
    20px -20px 0 0 currentColor,
    28px 0 0 0 currentColor,
    20px 20px 0 0 currentColor,
    0 28px 0 0 currentColor,
    -20px 20px 0 0 currentColor,
    -28px 0 0 0 currentColor,
    -20px -20px 0 0 currentColor;
  filter: drop-shadow(0 0 3px currentColor);
}

.firework-burst::after {
  background: radial-gradient(circle, currentColor 0%, transparent 70%);
  opacity: 0.45;
  transform: scale(2.2);
}

@keyframes firework-burst {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.15);
  }

  12% {
    opacity: 1;
  }

  42% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(1.15);
  }

  100% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(1.2);
  }
}
</style>
